import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, SegmentedControl, cn } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { Modal, Field, TextInput, Select, FormActions } from '../components/form';

const CADENCES = ['weekly', 'biweekly', 'monthly', 'bimonthly', 'quarterly', 'annual'];
const FEATURES: { fkey: string; kind: 'num' | 'bool' | 'media' }[] = [
  { fkey: 'checklist_points', kind: 'num' },
  { fkey: 'report_media', kind: 'media' },
  { fkey: 'deep_inspections_per_year', kind: 'num' },
  { fkey: 'ac_cleaning_per_year', kind: 'num' },
  { fkey: 'included_repair_minutes_per_month', kind: 'num' },
  { fkey: 'parts_margin_discount_pct', kind: 'num' },
  { fkey: 'post_storm_checks', kind: 'bool' },
  { fkey: 'remote_cctv', kind: 'bool' },
  { fkey: 'annual_compliance_check', kind: 'bool' },
  { fkey: 'guaranteed_2h_included', kind: 'bool' },
  { fkey: 'guaranteed_2h_option_price', kind: 'num' },
];

type Offer = {
  id: string; name: string;
  allowance_cadence: string | null; allowance_visits_per_period: number | null; allowance_minutes_per_period: number | null;
  allowance_carryover: boolean | null; allowance_carryover_cap: number | null; allowance_anchor: string | null;
  allowance_visit_overage: string | null; allowance_minute_overage: string | null; allowance_month_end: string | null; allowance_overage_rate_id: string | null;
  checklist_template_id: string | null;
};

export function CommercialSettings() {
  const { t } = useTranslation();
  const { role } = useOrg();
  const [tab, setTab] = useState(0);
  const tabs = ['cs.offers', 'cs.zones', 'cs.callout', 'cs.transparency', 'cs.history'];
  const canAdmin = ['org_admin', 'superadmin'].includes(role);

  return (
    <Layout header={<Topbar />}>
      <div className="flex flex-col gap-[18px] px-7 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('cs.title')}</h1>
        {!canAdmin && <Card className="p-4 text-sm text-muted">{t('cs.adminOnly')}</Card>}
        <SegmentedControl items={tabs.map((k) => t(k))} active={tab} onChange={setTab} />
      </div>
      <div className="flex flex-col gap-[18px] px-7 pb-7 pt-[18px]">
        {tab === 0 && <OffersTab />}
        {tab === 1 && <ZonesTab />}
        {tab === 2 && <CalloutTab />}
        {tab === 3 && <TransparencyTab />}
        {tab === 4 && <HistoryTab />}
      </div>
    </Layout>
  );
}

// ── Offers (price + per-room + features + allowance + checklist) ─────────────
function OffersTab() {
  const { t } = useTranslation();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [edit, setEdit] = useState<Offer | null>(null);
  const load = useCallback(async () => {
    const { data } = await supabase.from('catalogue_offers')
      .select('id,name,allowance_cadence,allowance_visits_per_period,allowance_minutes_per_period,allowance_carryover,allowance_carryover_cap,allowance_anchor,allowance_visit_overage,allowance_minute_overage,allowance_month_end,allowance_overage_rate_id,checklist_template_id')
      .eq('kind', 'subscription').is('deleted_at', null).order('name');
    setOffers((data as any) ?? []);
  }, []);
  useEffect(() => { void load(); }, [load]);
  return (
    <Card className="overflow-hidden p-0">
      <table className="w-full border-collapse text-sm">
        <thead><tr className="text-left text-xs text-muted"><th className="px-5 py-3 font-medium">{t('nav.catalogue')}</th><th className="px-2 py-3 font-medium">{t('cs.visits')}</th><th className="px-2 py-3 font-medium">{t('cs.minutes')}</th><th className="px-5 py-3" /></tr></thead>
        <tbody>
          {offers.map((o) => (
            <tr key={o.id} className="border-t border-divider hover:bg-bg">
              <td className="px-5 py-3.5 font-medium">{o.name}</td>
              <td className="px-2 py-3.5 font-mono text-muted">{o.allowance_visits_per_period ?? '—'} / {o.allowance_cadence ?? '—'}</td>
              <td className="px-2 py-3.5 font-mono text-muted">{o.allowance_minutes_per_period ?? '—'}</td>
              <td className="px-5 py-3.5 text-right"><Button variant="ghost" size="sm" onClick={() => setEdit(o)}>{t('form.edit')}</Button></td>
            </tr>
          ))}
          {offers.length === 0 && <tr><td colSpan={4} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>}
        </tbody>
      </table>
      {edit && <OfferEditor offer={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); void load(); }} />}
    </Card>
  );
}

function OfferEditor({ offer, onClose, onSaved }: { offer: Offer; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const [price, setPrice] = useState({ base: '', rooms: '3', extra: '' });
  const [feat, setFeat] = useState<Record<string, string>>({});
  const [allw, setAllw] = useState({ cadence: offer.allowance_cadence ?? 'monthly', visits: String(offer.allowance_visits_per_period ?? 0), minutes: String(offer.allowance_minutes_per_period ?? 0) });
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([]);
  const [tpl, setTpl] = useState(offer.checklist_template_id ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      const [p, f, tp] = await Promise.all([
        supabase.from('catalogue_offer_prices').select('base_price,rooms_included,extra_room_price').eq('offer_id', offer.id).eq('currency', currency).is('deleted_at', null).maybeSingle(),
        supabase.from('offer_features').select('feature_key,num_value,text_value,bool_value').eq('offer_id', offer.id).is('deleted_at', null),
        supabase.from('checklist_templates').select('id,name').is('deleted_at', null).order('name'),
      ]);
      if (p.data) setPrice({ base: String(p.data.base_price ?? ''), rooms: String(p.data.rooms_included ?? 3), extra: String(p.data.extra_room_price ?? '') });
      const fm: Record<string, string> = {};
      for (const row of (f.data as any[]) ?? []) fm[row.feature_key] = row.text_value ?? (row.bool_value != null ? String(row.bool_value) : row.num_value != null ? String(row.num_value) : '');
      setFeat(fm);
      setTemplates((tp.data as any) ?? []);
    })();
  }, [offer.id, currency]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      // price + per-room
      if (price.base.trim() !== '') {
        const r = await supabase.rpc('set_offer_price', { p_org: orgId, p_offer: offer.id, p_currency: currency, p_base: Number(price.base), p_rooms_included: Number(price.rooms) || 3, p_extra_room: Number(price.extra) || 0 });
        if (r.error) throw new Error(r.error.message);
      }
      // features
      for (const f of FEATURES) {
        const v = feat[f.fkey];
        if (v === undefined || v === '') continue;
        const args: any = { p_org: orgId, p_offer: offer.id, p_key: f.fkey, p_num: null, p_text: null, p_bool: null };
        if (f.kind === 'num') args.p_num = Number(v);
        else if (f.kind === 'bool') args.p_bool = v === 'true';
        else args.p_text = v;
        const r = await supabase.rpc('set_offer_feature', args);
        if (r.error) throw new Error(f.fkey + ': ' + r.error.message);
      }
      // allowance (preserve the offer's other allowance settings)
      const al = await supabase.rpc('set_offer_allowance', {
        p_offer: offer.id, p_cadence: allw.cadence, p_visits: Number(allw.visits) || 0, p_minutes: Number(allw.minutes) || 0,
        p_carryover: offer.allowance_carryover ?? false, p_carryover_cap: offer.allowance_carryover_cap ?? 0,
        p_anchor: offer.allowance_anchor ?? 'contract_start', p_visit_overage: offer.allowance_visit_overage ?? 'all_or_nothing',
        p_minute_overage: offer.allowance_minute_overage ?? 'prorata', p_month_end: offer.allowance_month_end ?? 'last_day',
        p_overage_rate: offer.allowance_overage_rate_id ?? null,
      });
      if (al.error) throw new Error(al.error.message);
      // checklist bind
      if (tpl !== (offer.checklist_template_id ?? '')) {
        const r = await supabase.rpc('set_offer_checklist', { p_org: orgId, p_offer: offer.id, p_template: tpl || null });
        if (r.error) throw new Error(r.error.message);
      }
      onSaved();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title={`${t('form.edit')} — ${offer.name}`} wide>
      <form onSubmit={save} className="flex flex-col gap-4 p-5">
        {err && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err}</div>}
        <span className="eyebrow">{t('cs.priceSection')}</span>
        <div className="grid grid-cols-3 gap-3">
          <Field label={`${t('cs.basePrice')} (${currency})`}><TextInput type="number" value={price.base} onChange={(e) => setPrice({ ...price, base: e.target.value })} /></Field>
          <Field label={t('cs.roomsIncluded')}><TextInput type="number" value={price.rooms} onChange={(e) => setPrice({ ...price, rooms: e.target.value })} /></Field>
          <Field label={`${t('cs.extraRoom')} (${currency})`}><TextInput type="number" value={price.extra} onChange={(e) => setPrice({ ...price, extra: e.target.value })} /></Field>
        </div>
        <span className="eyebrow">{t('cs.allowanceSection')}</span>
        <div className="grid grid-cols-3 gap-3">
          <Field label={t('cs.cadence')}><Select value={allw.cadence} onChange={(e) => setAllw({ ...allw, cadence: e.target.value })}>{CADENCES.map((c) => <option key={c} value={c}>{t('cadence.' + c)}</option>)}</Select></Field>
          <Field label={t('cs.visitsPerPeriod')}><TextInput type="number" value={allw.visits} onChange={(e) => setAllw({ ...allw, visits: e.target.value })} /></Field>
          <Field label={t('cs.minutesPerPeriod')}><TextInput type="number" value={allw.minutes} onChange={(e) => setAllw({ ...allw, minutes: e.target.value })} /></Field>
        </div>
        <span className="eyebrow">{t('cs.featuresSection')}</span>
        <div className="grid grid-cols-2 gap-3">
          {FEATURES.map((f) => (
            <Field key={f.fkey} label={t('feat.' + f.fkey)}>
              {f.kind === 'bool' ? (
                <Select value={feat[f.fkey] ?? 'false'} onChange={(e) => setFeat({ ...feat, [f.fkey]: e.target.value })}><option value="true">{t('common.yes')}</option><option value="false">{t('common.no')}</option></Select>
              ) : f.kind === 'media' ? (
                <Select value={feat[f.fkey] ?? 'photo'} onChange={(e) => setFeat({ ...feat, [f.fkey]: e.target.value })}><option value="photo">{t('feat.media_photo')}</option><option value="photo_video">{t('feat.media_photo_video')}</option></Select>
              ) : (
                <TextInput type="number" value={feat[f.fkey] ?? ''} onChange={(e) => setFeat({ ...feat, [f.fkey]: e.target.value })} />
              )}
            </Field>
          ))}
        </div>
        <span className="eyebrow">{t('cs.checklistSection')}</span>
        <Field label={t('nav.checklists')}>
          <Select value={tpl} onChange={(e) => setTpl(e.target.value)}><option value="">{t('form.none')}</option>{templates.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</Select>
        </Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Zones ────────────────────────────────────────────────────────────────────
function ZonesTab() {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [zones, setZones] = useState<{ id: string; name: string; areas: string[]; active: boolean }[]>([]);
  const [edit, setEdit] = useState<{ name: string; areas: string; active: boolean } | null>(null);
  const load = useCallback(async () => {
    const { data } = await supabase.from('service_zones').select('id,name,areas,active').is('deleted_at', null).order('name');
    setZones((data as any) ?? []);
  }, []);
  useEffect(() => { void load(); }, [load]);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!edit) return;
    await supabase.rpc('set_service_zone', { p_org: orgId, p_name: edit.name.trim(), p_areas: edit.areas.split(',').map((s) => s.trim()).filter(Boolean), p_active: edit.active });
    setEdit(null); void load();
  }
  return (
    <Card className="overflow-hidden p-0">
      <div className="flex justify-end p-3"><Button size="sm" onClick={() => setEdit({ name: '', areas: '', active: true })}>+ {t('cs.newZone')}</Button></div>
      <table className="w-full border-collapse text-sm">
        <tbody>
          {zones.map((z) => (
            <tr key={z.id} className="border-t border-divider first:border-0">
              <td className="px-5 py-3 font-medium">{z.name}</td>
              <td className="px-2 py-3 text-muted">{z.areas?.join(', ')}</td>
              <td className="px-2 py-3">{z.active ? t('cs.active') : t('cs.inactive')}</td>
              <td className="px-5 py-3 text-right"><Button variant="ghost" size="sm" onClick={() => setEdit({ name: z.name, areas: (z.areas ?? []).join(', '), active: z.active })}>{t('form.edit')}</Button></td>
            </tr>
          ))}
          {zones.length === 0 && <tr><td className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>}
        </tbody>
      </table>
      {edit && (
        <Modal open onClose={() => setEdit(null)} title={t('cs.zone')}>
          <form onSubmit={save} className="flex flex-col gap-4 p-5">
            <Field label={t('form.name')} required><TextInput value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} autoFocus /></Field>
            <Field label={t('cs.areas')} hint={t('cs.areasHint')}><TextInput value={edit.areas} onChange={(e) => setEdit({ ...edit, areas: e.target.value })} /></Field>
            <Field label={t('cs.status')}><Select value={String(edit.active)} onChange={(e) => setEdit({ ...edit, active: e.target.value === 'true' })}><option value="true">{t('cs.active')}</option><option value="false">{t('cs.inactive')}</option></Select></Field>
            <FormActions onCancel={() => setEdit(null)} />
          </form>
        </Modal>
      )}
    </Card>
  );
}

// ── Call-out pricing ──────────────────────────────────────────────────────────
function CalloutTab() {
  const { t } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const [f, setF] = useState({ base: '', extra: '', evening: '', weekend: '', holiday: '', open: true });
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    supabase.from('callout_pricing').select('*').is('deleted_at', null).maybeSingle().then(({ data }) => {
      if (data) setF({ base: String(data.base_fee), extra: String(data.extra_hour_rate), evening: String(data.evening_surcharge_pct), weekend: String(data.weekend_surcharge_pct), holiday: String(data.holiday_surcharge_pct), open: data.open_to_non_subscribers });
    });
  }, []);
  async function save(e: React.FormEvent) {
    e.preventDefault(); setBusy(true);
    await supabase.rpc('set_callout_pricing', { p_org: orgId, p_base: Number(f.base) || 0, p_extra: Number(f.extra) || 0, p_evening: Number(f.evening) || 0, p_weekend: Number(f.weekend) || 0, p_holiday: Number(f.holiday) || 0, p_currency: currency, p_open: f.open });
    setBusy(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
  }
  return (
    <Card className="max-w-[640px] p-5">
      <form onSubmit={save} className="flex flex-col gap-4">
        {saved && <div className="rounded-md bg-st-done-bg px-3 py-2 text-sm text-st-done-fg">{t('common.saved')}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label={`${t('cs.calloutBase')} (${currency})`}><TextInput type="number" value={f.base} onChange={(e) => setF({ ...f, base: e.target.value })} /></Field>
          <Field label={`${t('cs.calloutExtra')} (${currency})`}><TextInput type="number" value={f.extra} onChange={(e) => setF({ ...f, extra: e.target.value })} /></Field>
          <Field label={t('cs.eveningPct')}><TextInput type="number" value={f.evening} onChange={(e) => setF({ ...f, evening: e.target.value })} /></Field>
          <Field label={t('cs.weekendPct')}><TextInput type="number" value={f.weekend} onChange={(e) => setF({ ...f, weekend: e.target.value })} /></Field>
          <Field label={t('cs.holidayPct')}><TextInput type="number" value={f.holiday} onChange={(e) => setF({ ...f, holiday: e.target.value })} /></Field>
          <Field label={t('cs.openNonSub')}><Select value={String(f.open)} onChange={(e) => setF({ ...f, open: e.target.value === 'true' })}><option value="true">{t('common.yes')}</option><option value="false">{t('common.no')}</option></Select></Field>
        </div>
        <div className="flex justify-end"><Button type="submit" variant="accent" disabled={busy}>{busy ? t('common.saving') : t('common.save')}</Button></div>
      </form>
    </Card>
  );
}

// ── Transparency (margin / approval / warranty) ──────────────────────────────
function TransparencyTab() {
  const { t } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const [f, setF] = useState({ margin: '', approval: '', warranty: '', requireAsset: false });
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    (async () => {
      const o = await supabase.from('organizations').select('default_parts_margin_pct,quote_written_approval_threshold').limit(1).maybeSingle();
      const w = await supabase.from('wo_effect_rules').select('config').eq('effect', 'warranty_timer').is('deleted_at', null).limit(1).maybeSingle();
      setF({
        margin: String(o.data?.default_parts_margin_pct ?? 20), approval: String(o.data?.quote_written_approval_threshold ?? 1500000),
        warranty: String((w.data?.config as any)?.days ?? 90), requireAsset: !!(w.data?.config as any)?.require_asset,
      });
    })();
  }, []);
  async function save(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      let r = await supabase.rpc('set_parts_margin', { p_org: orgId, p_pct: Number(f.margin) }); if (r.error) throw new Error(r.error.message);
      r = await supabase.rpc('set_quote_approval_threshold', { p_org: orgId, p_amount: Number(f.approval) }); if (r.error) throw new Error(r.error.message);
      r = await supabase.rpc('set_warranty_days', { p_org: orgId, p_days: Number(f.warranty), p_require_asset: f.requireAsset }); if (r.error) throw new Error(r.error.message);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <Card className="max-w-[640px] p-5">
      <form onSubmit={save} className="flex flex-col gap-4">
        {saved && <div className="rounded-md bg-st-done-bg px-3 py-2 text-sm text-st-done-fg">{t('common.saved')}</div>}
        {err && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err}</div>}
        <Field label={t('cs.defaultMargin')} hint={t('cs.marginHint')}><TextInput type="number" value={f.margin} onChange={(e) => setF({ ...f, margin: e.target.value })} /></Field>
        <Field label={`${t('cs.approvalThreshold')} (${currency})`} hint={t('cs.approvalHint')}><TextInput type="number" value={f.approval} onChange={(e) => setF({ ...f, approval: e.target.value })} /></Field>
        <Field label={t('cs.warrantyDays')} hint={t('cs.warrantyHint')}><TextInput type="number" value={f.warranty} onChange={(e) => setF({ ...f, warranty: e.target.value })} /></Field>
        <Field label={t('cs.warrantyRequireAsset')}><Select value={String(f.requireAsset)} onChange={(e) => setF({ ...f, requireAsset: e.target.value === 'true' })}><option value="false">{t('common.no')}</option><option value="true">{t('common.yes')}</option></Select></Field>
        <div className="flex justify-end"><Button type="submit" variant="accent" disabled={busy}>{busy ? t('common.saving') : t('common.save')}</Button></div>
      </form>
    </Card>
  );
}

// ── Change history ───────────────────────────────────────────────────────────
function HistoryTab() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<{ id: string; entity_type: string; field: string; old_value: string | null; new_value: string | null; changed_at: string }[]>([]);
  useEffect(() => {
    supabase.from('allowance_setting_changes').select('id,entity_type,field,old_value,new_value,changed_at').order('changed_at', { ascending: false }).limit(100).then(({ data }) => setRows((data as any) ?? []));
  }, []);
  return (
    <Card className="overflow-hidden p-0">
      <table className="w-full border-collapse text-sm">
        <thead><tr className="text-left text-xs text-muted"><th className="px-5 py-3 font-medium">{t('cs.when')}</th><th className="px-2 py-3 font-medium">{t('cs.entity')}</th><th className="px-2 py-3 font-medium">{t('cs.field')}</th><th className="px-2 py-3 font-medium">{t('cs.old')}</th><th className="px-5 py-3 font-medium">{t('cs.new')}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-divider">
              <td className="px-5 py-2.5 font-mono text-xs text-muted">{new Date(r.changed_at).toLocaleString(i18n.language)}</td>
              <td className="px-2 py-2.5 text-muted">{r.entity_type}</td>
              <td className="px-2 py-2.5 font-medium">{r.field}</td>
              <td className="px-2 py-2.5 font-mono text-xs text-muted">{r.old_value ?? '—'}</td>
              <td className="px-5 py-2.5 font-mono text-xs">{r.new_value ?? '—'}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}
